import { PendoProfile } from "./pendo-profile.model";
import type { PendoProfileSource } from "./sources/pendo-profile-source";

/**
 * The best profile the sources have given so far.
 *
 * The most trusted source that has answered decides the ids. Every source
 * that agrees on those ids then fills in, in trust order, only what the more
 * trusted ones don't know; one that disagrees is ignored.
 */
export class PendoProfileBuilder {
    private readonly _answers = new Map<PendoProfileSource, PendoProfile>();
    private _profile?: PendoProfile;

    /** @param _trustOrder most trusted first */
    constructor(private readonly _trustOrder: readonly PendoProfileSource[]) {}

    /** Adds what `source` knows, and gives the profile if that changed it. */
    add(
        source: PendoProfileSource,
        answer: PendoProfile,
    ): PendoProfile | undefined {
        const previous = this._profile;
        this._answers.set(source, answer);
        this._profile = this._resolve();
        const changed =
            !previous || canonical(previous) !== canonical(this._profile);
        return changed ? this._profile : undefined;
    }

    private _resolve(): PendoProfile {
        const ranked = this._trustOrder.flatMap((source) => {
            const answer = this._answers.get(source);
            return answer ? [answer] : [];
        });
        return ranked
            .filter((answer) => PendoProfile.hasSameIds(ranked[0], answer))
            .reduce(PendoProfile.fillMissing);
    }
}

/** Serializes with sorted keys, so equal profiles compare equal whatever order their properties were filled in. */
function canonical(profile: PendoProfile): string {
    return JSON.stringify(profile, (_key, value: unknown) =>
        value !== null && typeof value === "object" && !Array.isArray(value)
            ? Object.fromEntries(
                  Object.entries(value).sort(([a], [b]) => a.localeCompare(b)),
              )
            : value,
    );
}
